import React, {
  ChangeEvent,
  useContext,
  useEffect,
  useState,
  MouseEvent,
  FormEvent,
} from 'react';
import { useForm } from 'react-hook-form';

import {
  ButtonsWrapper,
  InputWrapper,
  ErrorMsg,
  Input,
  Label,
  Form,
  RechargeSuccess,
  ServerError,
} from '../ModalsManager.styled';
import { StyledBtnWithSound } from '../../../sounds/StyledBtnWithSound';
import {
  RechargeSigners,
  ServerMessage,
  SignerMessage,
  SocketEmit,
  SocketOn,
  SoundType,
  UserInfo,
  WithdrawSigners,
} from '../../../types.ds';
import { socket } from '../../../server/socket';
import { game } from '../../../store/game';
import { PlayerContext } from '../../../contexts/playerContext';
import { LAMPORTS_PER_SOL, PublicKey } from '@solana/web3.js';
import { updateBalance } from '../../../utils/updateBalance';
import { transactionHandler } from '../../../utils/createTransaction';
import { withdrawBalance } from '../../../utils/withdrawBalance';
import { generateWithDrawSigners } from '../../../utils/generateWithDrawSigners';
import { generateRechargeSigners } from '../../../utils/generateRechargeSigners';
import _ from 'lodash';
import { getUserInfo } from '../../../utils/getUserInfo';

interface FormValues {
  balance: number;
}

export const BalanceForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setError,
  } = useForm<FormValues>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [amount, setAmount] = useState<number>(0);
  const [withdrawSuccess, setWithDrawSuccess] = useState<boolean>(false);
  const [rechargeSuccess, setRechargeSuccess] = useState<boolean>(false);
  const [serverMessage, setServerMessage] = useState<string | null>(null);
  const playerContext = useContext(PlayerContext);

  const onCancel = () => {
    reset();
    setAmount(0);
    setDisabled(false);
    game.modalUpdate(true);
    setWithDrawSuccess(false);
    setRechargeSuccess(false);
    setServerMessage(null);
  };

  function isValidNumber(input: string): boolean {
    // Regex to match a number with up to 2 decimal places
    const regex = /^\d+(\.\d{1,2})?$/;
    return regex.test(input);
  }

  const amountHandler = (e: FormEvent<HTMLInputElement>) => {
    e.preventDefault();
    const input = +e.currentTarget.value;
    setAmount(input);
  };

  async function rechargeHandler(amount: number) {
    if (amount < 2) {
      setError('balance', { message: 'Minimum amount should be 2' });
      setDisabled(false);
      return;
    } else {
      setError('balance', { message: '' });
    }

    if (!isValidNumber(amount.toString())) {
      setError('balance', {
        message: 'Maximum 2 decimal places allowed.',
      });
      setDisabled(false);
      return;
    } else {
      setError('balance', { message: '' });
    }
    if (!playerContext?.walletBalance) {
      setDisabled(false);
      setError('balance', {
        message: 'Something went wrong.',
      });
      return;
    }
    const balInSol = playerContext?.walletBalance / LAMPORTS_PER_SOL;
    if (amount / 1000 > +balInSol.toFixed(3)) {
      setError('balance', { message: 'Insufficient funds.' });
      setDisabled(false);

      return;
    } else {
      setError('balance', { message: '' });
    }

    if (
      playerContext &&
      playerContext.anchorWallet &&
      process.env.REACT_APP_ADMIN_PUB_KEY
    ) {
      try {
        const getRechargeSigners: RechargeSigners | null | undefined =
          await generateRechargeSigners();
        if (!getRechargeSigners?.signers) {
          setServerMessage('Failed to generate Signers. Try again!');
          return;
        }

        const tokens = (amount / 1000).toFixed(4);
        const RECEIVER_PUB_KEY = new PublicKey(
          process.env.REACT_APP_ADMIN_PUB_KEY
        );

        const transaction = await transactionHandler(
          playerContext.anchorWallet?.publicKey,
          RECEIVER_PUB_KEY,
          tokens
        );
        if (!transaction) {
          setDisabled(false);
          setAmount(0);
          throw new Error('Invalid Transaction.');
        }

        try {
          const transactionHash = await playerContext.wallet.sendTransaction(
            transaction,
            playerContext.connection
          );

          // Wait for transaction confirmation
          const confirmation = await playerContext.connection.confirmTransaction(transactionHash, 'confirmed');

          if (confirmation.value.err) {
            setServerMessage('Transaction failed. Please try again.');
            setDisabled(false);
            return;
          }

          // Transaction confirmed successfully, proceed with the rest of the logic
          try {
            const user = await updateBalance(
              'action-recharge',
              playerContext.playerId,
              amount,
              transactionHash,
              transaction,
              getRechargeSigners
            );
            if (user && user?.userData) {
              const balance = +user.userData.balance.toFixed(2);
              playerContext.withdrawlBalanceHandler(balance);
              const freeTokenBalance = +user.userData.freeTokens.toFixed(2);
              const totalBalance =
                freeTokenBalance > 0 ? balance + freeTokenBalance : balance;
              playerContext.tokenBalanceHandler(
                // +user.userData.balance.toFixed(2)
                totalBalance
              );
              playerContext.initialTokenBalanceHandler(
                // +user.userData.balance.toFixed(2)
                totalBalance
              );
              // playerContext.tokenBalanceHandler(balance);
              // playerContext.initialTokenBalanceHandler(balance);
              game.emit[SocketEmit.TopupBalance](amount);
            } else {
              setServerMessage(
                (user && user.message) || 'Something went wrong.'
              );
              setAmount(0);
              setRechargeSuccess(false);
              setDisabled(false);
              reset();
              return;
            }
            setAmount(0);
            setRechargeSuccess(true);
            setDisabled(false);
            reset();
          } catch (error: any) {
            setAmount(0);
            setDisabled(false);
            reset();
          }
        } catch (error: any) {
          setServerMessage('Transaction failed: ' + (error.message || 'Unknown error'));
          setDisabled(false);
          reset();
        }
      } catch (error: any) {
        setServerMessage(error.message || 'Something went wrong.');
        reset();
      }
    }
  }

  const onSubmit = async (data: FormValues) => {
    setDisabled(true);
    setRechargeSuccess(false);
    setWithDrawSuccess(false);
    setServerMessage(null);
    rechargeHandler(amount);
  };

  const topUpHandler = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setDisabled(true);
    setRechargeSuccess(false);
    setWithDrawSuccess(false);
    setServerMessage(null);

    rechargeHandler(amount);
  };

  const withdrawHandler = async (e: MouseEvent) => {
    e.preventDefault();
    setDisabled(true);
    setRechargeSuccess(false);
    setWithDrawSuccess(false);
    setServerMessage(null);
    const program = playerContext?.program;
    if (!program) {
      setServerMessage('Program not found');
      return;
    }

    try {
      if (!playerContext.anchorWallet?.publicKey || !playerContext.program) {
        throw new Error(
          'Wallet is not connected or user public key is undefined'
        );
      }

      const user = await getUserInfo(playerContext.anchorWallet?.publicKey.toString());
      if (user && user.userData?.blocked) {
        setError('balance', { message: 'You are blocked by admin.' });
        setDisabled(false);
        return;
      }

      if (amount > playerContext.tokenBalance) {
        setError('balance', { message: 'Insufficient funds.' });
        setDisabled(false);
        return;
      } else {
        setError('balance', { message: '' });
      }

      if (!isValidNumber(amount.toString())) {
        setError('balance', {
          message: 'Maximum 2 decimal places allowed.',
        });
        setDisabled(false);
        return;
      } else {
        setError('balance', { message: '' });

        if (amount < 1 || !amount) {
          setError('balance', { message: 'Minimum withdraw amount is 1' });
          setDisabled(false);
          return;
        } else {
          setError('balance', { message: '' });
        }

        if (playerContext.withdrawlBalance < amount) {
          setError('balance', {
            message: `Insufficient funds! Max withdrawl allowed: ${playerContext.withdrawlBalance}`,
          });
          setDisabled(false);
          return;
        } else {
          setError('balance', { message: '' });
        }

        const nounceKeys: WithdrawSigners | undefined | null =
          await generateWithDrawSigners(
            playerContext.playerId,
            playerContext.anchorWallet.publicKey.toString(),
            amount
          );
        if (!nounceKeys?.signers) {
          setServerMessage('Failed to generate nounce!');
          return;
        }
        const tx = await withdrawBalance(
          amount,
          program,
          playerContext.anchorWallet.publicKey,
          nounceKeys
        );

        if (!tx) {
          setError('balance', {
            message: 'Something went wrong.',
          });
          setAmount(0);
          setDisabled(false);
          reset();
          return;
        }

        if (tx) {
          try {
            // Wait for transaction confirmation
            const confirmation = await playerContext.connection.confirmTransaction(tx, 'confirmed');

            if (confirmation.value.err) {
              setServerMessage('Transaction failed. Please try again.');
              setDisabled(false);
              setAmount(0);
              reset();
              return;
            }

            // Transaction confirmed successfully, proceed with updating balance
            const user: UserInfo | ServerMessage | undefined = await updateBalance(
              'action-withdraw',
              playerContext.playerId,
              amount,
              tx,
              undefined,
              nounceKeys
            );

            if (!user) {
              setServerMessage('Something went wrong!');
              return;
            }

            if (user && user.userData) {
              const balance = +user.userData.balance.toFixed(2);
              const freeTokenBalance = +user.userData.freeTokens.toFixed(2);
              const totalBalance = freeTokenBalance > 0
                ? balance + freeTokenBalance
                : balance;

              playerContext.tokenBalanceHandler(totalBalance);
              playerContext.initialTokenBalanceHandler(totalBalance);
              game.emit[SocketEmit.BalanceWithdrawl](amount);
            } else {
              setServerMessage(user.message || 'Something went wrong');
              setWithDrawSuccess(false);
              setAmount(0);
              setDisabled(false);
              reset();
              return;
            }
            setAmount(0);
            setDisabled(false);
            setWithDrawSuccess(true);
            reset();
          } catch (error: any) {
            setServerMessage('Transaction failed: ' + (error.message || 'Unknown error'));
            setAmount(0);
            setDisabled(false);
            reset();
          }
        }
      }
    } catch (err: any) {
      setError('balance', {
        message: 'Something went wrong.',
      });
      setWithDrawSuccess(false);
      setAmount(0);
      setDisabled(false);
      setServerMessage(err.message || 'Something went wrong.');
      reset();
      return;
    }
  };

  // useEffect(() => {
  //   const handleTopUp = () => {
  //     reset();
  //     setDisabled(false);
  //     game.modalUpdate(true);
  //   };

  //   socket.on(SocketOn.BalanceToppedUp, handleTopUp);

  //   return () => {
  //     socket.off(SocketOn.BalanceToppedUp, handleTopUp);
  //   };
  // }, [reset]);

  return (
    <Form
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleSubmit(onSubmit)}
      onClick={(e) => e.stopPropagation()}
    >
      <InputWrapper>
        <Input
          autoComplete="off"
          className={`${watch('balance') ? 'filled' : ''}`}
          type="number"
          step={0.01}
          onKeyPress={(event) => {
            if (event.key === '+' || event.key === '-') {
              event.preventDefault();
            }
          }}
          {...register('balance', {})}
          onInput={(e) => amountHandler(e)}
        // onChange={(e) => amountHandler(e)}
        />
        <Label>amount:</Label>
      </InputWrapper>

      <ButtonsWrapper>
        <StyledBtnWithSound
          soundType={SoundType.Click}
          type="button"
          className="button buttonBlue"
          disabled={disabled}
          onClick={(e) => topUpHandler(e)}
        >
          RECHARGE
        </StyledBtnWithSound>
        {
          playerContext?.blocked != true && (
            <StyledBtnWithSound
              soundType={SoundType.Click}
              type="button"
              className="button buttonBlue"
              disabled={disabled}
              onClick={(e) => withdrawHandler(e)}
            >
              Withdraw
            </StyledBtnWithSound>
          )
        }
      </ButtonsWrapper>
      <ButtonsWrapper>
        <StyledBtnWithSound
          soundType={SoundType.Click}
          type="button"
          className="button buttonRed"
          onClick={onCancel}
        >
          Cancel
        </StyledBtnWithSound>
      </ButtonsWrapper>
      {withdrawSuccess && (
        <RechargeSuccess>Withdrawl successful ✅.</RechargeSuccess>
      )}
      {rechargeSuccess && (
        <RechargeSuccess>Recharge successful ✅.</RechargeSuccess>
      )}
      {errors.balance && <ServerError>{errors.balance.message}</ServerError>}

      {serverMessage && <ServerError>{serverMessage}</ServerError>}
    </Form>
  );
};
